import { useRef, useSyncExternalStore } from "react";
import { getApiError } from "tm-shared/api";

export enum QueueTaskStatus {
    Pending = "pending",
    Running = "running",
    Completed = "completed",
    Error = "error",
    Idle = "idle",
}

export type TaskMetadata = {
    type?: string;
};

export type QueueTaskInternal = {
    id: string;
    metadata?: TaskMetadata;
    label: string;
    status: QueueTaskStatus;
    promiseFactory: () => Promise<unknown>;
    error?: unknown;
};

export type QueueTaskSnapshot = {
    id: string;
    metadata?: TaskMetadata;
    label: string;
    status: QueueTaskStatus;
    error?: string;
};

export interface QueueProgress {
    tasks: QueueTaskSnapshot[];
    isRunning: boolean;
    completedCount: number;
    totalCount: number;
    lastErrorIndex?: number;
    lastCompletedIndex?: number; // Додаємо трекінг останнього успішного індексу
}

interface Options {
    stopOnError?: boolean;
    startFromIndex?: number;
}

export interface Queue {
    readonly progress: QueueProgress;
    add(
        label: string,
        promiseFactory: () => Promise<unknown>,
        metadata?: TaskMetadata,
    ): string;
    runParallel(): Promise<void>;
    runSuccessively(opts?: Options): Promise<void>;
    addAndRun<T>(
        label: string,
        promiseFactory: () => Promise<T>,
        metadata?: TaskMetadata,
    ): Promise<T>;
    clear(): void;
    subscribe(listener: () => void): () => void;
    getSnapshot(): QueueProgress;
}

export const createQueue = (): Queue => {
    const tasks = new Map<string, QueueTaskInternal>();
    let queueRunning = false;
    let progress: QueueProgress = {
        tasks: [],
        isRunning: false,
        completedCount: 0,
        totalCount: 0,
    };

    const listeners = new Set<() => void>();
    const notify = () => listeners.forEach((l) => l());

    const buildSnapshot = (): QueueProgress => {
        const taskArray = Array.from(tasks.values());

        const completedCount = taskArray.filter(
            (t) =>
                t.status === QueueTaskStatus.Completed ||
                t.status === QueueTaskStatus.Error,
        ).length;

        const snapshotTasks: QueueTaskSnapshot[] = taskArray.map(
            ({ id, label, status, metadata, error }) => {
                let errorMessage = "";

                if (error) {
                    const { code, message } = getApiError(error);
                    errorMessage = `${code}: ${message}`;
                }

                return {
                    id,
                    label,
                    status,
                    ...(metadata && { metadata }),
                    ...(error && { error: errorMessage }),
                };
            },
        );

        return {
            tasks: snapshotTasks,
            isRunning:
                queueRunning ||
                taskArray.some((t) => t.status === QueueTaskStatus.Running),
            completedCount,
            totalCount: taskArray.length,
            ...(progress.lastErrorIndex !== undefined && {
                lastErrorIndex: progress.lastErrorIndex,
            }),
            ...(progress.lastCompletedIndex !== undefined && {
                lastCompletedIndex: progress.lastCompletedIndex,
            }),
        };
    };

    const updateProgress = () => {
        progress = buildSnapshot();
        notify();
    };

    const executeTask = async (task: QueueTaskInternal) => {
        try {
            task.status = QueueTaskStatus.Running;
            updateProgress();
            await task.promiseFactory();
            task.status = QueueTaskStatus.Completed;
            task.error = undefined;
        } catch (e) {
            console.error(`Error executing ${task.label}:`, e);
            task.status = QueueTaskStatus.Error;
            task.error = e;
        }
        updateProgress();
    };

    const add = (
        label: string,
        promiseFactory: () => Promise<unknown>,
        metadata?: TaskMetadata,
    ) => {
        const id =
            globalThis.crypto?.randomUUID?.() ??
            `task-${Date.now()}-${Math.random()}`;
        tasks.set(id, {
            id,
            label,
            status: QueueTaskStatus.Pending,
            promiseFactory,
            ...(metadata && { metadata }),
        });
        updateProgress();
        return id;
    };

    const addAndRun = async <T>(
        label: string,
        promiseFactory: () => Promise<T>,
        metadata?: TaskMetadata,
    ): Promise<T> => {
        const id = add(label, promiseFactory, metadata);
        const task = tasks.get(id)!;
        try {
            task.status = QueueTaskStatus.Running;
            updateProgress();
            const res = await promiseFactory();
            task.status = QueueTaskStatus.Completed;
            task.error = undefined;
            updateProgress();
            return res;
        } catch (e) {
            task.status = QueueTaskStatus.Error;
            task.error = e;
            updateProgress();
            throw e;
        }
    };

    const runParallel = async () => {
        queueRunning = true;
        updateProgress();

        try {
            let pending = Array.from(tasks.values()).filter(
                (t) => t.status === QueueTaskStatus.Pending,
            );

            while (pending.length) {
                await Promise.allSettled(pending.map((t) => executeTask(t)));
                pending = Array.from(tasks.values()).filter(
                    (t) => t.status === QueueTaskStatus.Pending,
                );
            }
        } finally {
            queueRunning = false;
            updateProgress();
        }
    };

    const runSuccessively = async (opts: Options = {}) => {
        const { stopOnError = false, startFromIndex = 0 } = opts;

        queueRunning = true;
        updateProgress();

        try {
            let pending = Array.from(tasks.values()).filter(
                (t) => t.status === QueueTaskStatus.Pending,
            );

            while (pending.length > 0) {
                for (
                    let i = 0;
                    i < Math.min(startFromIndex, pending.length);
                    i++
                ) {
                    pending[i].status = QueueTaskStatus.Completed;
                }
                updateProgress();

                for (let i = startFromIndex; i < pending.length; i++) {
                    const task = pending[i];
                    await executeTask(task);

                    if (stopOnError && task.status === QueueTaskStatus.Error) {
                        progress = {
                            ...progress,
                            lastErrorIndex: i,
                        };
                        throw task.error;
                    }

                    if (task.status === QueueTaskStatus.Completed) {
                        progress = {
                            ...progress,
                            lastCompletedIndex: i,
                        };
                    }
                }

                pending = Array.from(tasks.values()).filter(
                    (t) => t.status === QueueTaskStatus.Pending,
                );
            }
        } finally {
            queueRunning = false;
            updateProgress();
        }
    };

    return {
        get progress() {
            return progress;
        },
        add,
        addAndRun,
        runParallel,
        runSuccessively,
        clear() {
            tasks.clear();
            queueRunning = false;
            progress = {
                tasks: [],
                isRunning: false,
                completedCount: 0,
                totalCount: 0,
            };
            notify();
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot() {
            return progress;
        },
    };
};

export const useAsyncTaskQueue = () => {
    const queueRef = useRef<Queue>(createQueue());
    const progress = useSyncExternalStore(
        queueRef.current.subscribe,
        queueRef.current.getSnapshot,
    );

    return { queue: queueRef.current, progress };
};
